import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  Wifi, Watch, MapPin, FlaskConical, Activity, AlertTriangle,
  ThermometerSun, Volume2, Wind, Gauge, Radio, Heart, Footprints,
  MapPinned, Shield, Users, Plus, RefreshCw, Loader2, Search,
  AlertCircle, CheckCircle, XCircle, Eye, FileText, Beaker,
  Skull, Flame, Droplets, Zap, CircleDot, User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SafetyMonitoringHub = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('sensors');
  const [loading, setLoading] = useState(false);

  // IoT Sensors State
  const [sensorDashboard, setSensorDashboard] = useState(null);
  const [sensorAlerts, setSensorAlerts] = useState([]);
  const [showAddSensor, setShowAddSensor] = useState(false);

  // Wearables State
  const [wearables, setWearables] = useState([]);
  const [workerStatus, setWorkerStatus] = useState([]);
  const [fallEvents, setFallEvents] = useState([]);

  // Geofencing State
  const [zones, setZones] = useState([]);
  const [geofenceAlerts, setGeofenceAlerts] = useState([]);
  const [showAddZone, setShowAddZone] = useState(false);

  // Chemicals State
  const [chemicals, setChemicals] = useState([]);
  const [chemicalInventory, setChemicalInventory] = useState([]);
  const [chemicalSearch, setChemicalSearch] = useState('');
  const [selectedChemical, setSelectedChemical] = useState(null);
  const [exposureRecords, setExposureRecords] = useState([]);

  useEffect(() => {
    fetchAllData();
    // Set up polling for real-time updates
    const interval = setInterval(fetchSensorData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSensorData(),
        fetchWearableData(),
        fetchZoneData(),
        fetchChemicalData()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSensorData = async () => {
    try {
      const [dashboardRes, alertsRes] = await Promise.all([
        axios.get(`${API}/safety-monitoring/sensors/dashboard`),
        axios.get(`${API}/safety-monitoring/sensors/alerts?unacknowledged_only=true`)
      ]);
      setSensorDashboard(dashboardRes.data);
      setSensorAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error('Error fetching sensor data:', error);
    }
  };

  const fetchWearableData = async () => {
    try {
      const [devicesRes, statusRes, fallsRes] = await Promise.all([
        axios.get(`${API}/safety-monitoring/wearables`),
        axios.get(`${API}/safety-monitoring/wearables/worker-status`),
        axios.get(`${API}/safety-monitoring/wearables/fall-events?unacknowledged_only=true`)
      ]);
      setWearables(devicesRes.data.devices || []);
      setWorkerStatus(statusRes.data.workers || []);
      setFallEvents(fallsRes.data.events || []);
    } catch (error) {
      console.error('Error fetching wearable data:', error);
    }
  };

  const fetchZoneData = async () => {
    try {
      const [zonesRes, alertsRes] = await Promise.all([
        axios.get(`${API}/safety-monitoring/zones`),
        axios.get(`${API}/safety-monitoring/zones/alerts?unacknowledged_only=true`)
      ]);
      setZones(zonesRes.data.zones || []);
      setGeofenceAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error('Error fetching zone data:', error);
    }
  };

  const fetchChemicalData = async () => {
    try {
      const [chemsRes, invRes, expRes] = await Promise.all([
        axios.get(`${API}/safety-monitoring/chemicals?limit=50`),
        axios.get(`${API}/safety-monitoring/chemicals/inventory`),
        axios.get(`${API}/safety-monitoring/chemicals/exposures?limit=20`)
      ]);
      setChemicals(chemsRes.data.chemicals || []);
      setChemicalInventory(invRes.data.inventory || []);
      setExposureRecords(expRes.data.records || []);
    } catch (error) {
      console.error('Error fetching chemical data:', error);
    }
  };

  const searchChemicals = async () => {
    if (!chemicalSearch.trim()) return;
    try {
      const response = await axios.get(`${API}/safety-monitoring/chemicals/sds-search?query=${encodeURIComponent(chemicalSearch)}`);
      setChemicals(response.data.results || []);
    } catch (error) {
      toast({ title: "Error", description: "Search failed", variant: "destructive" });
    }
  };

  const getAlertColor = (level) => {
    const colors = {
      'normal': 'bg-green-100 text-green-800',
      'caution': 'bg-yellow-100 text-yellow-800',
      'warning': 'bg-orange-100 text-orange-800',
      'danger': 'bg-red-100 text-red-800',
      'critical': 'bg-red-200 text-red-900 animate-pulse'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getZoneTypeColor = (type) => {
    const colors = {
      'safe_zone': 'bg-green-500',
      'restricted': 'bg-yellow-500',
      'hazardous': 'bg-orange-500',
      'confined_space': 'bg-red-500',
      'high_voltage': 'bg-purple-500',
      'chemical_storage': 'bg-pink-500',
      'construction': 'bg-blue-500',
      'exclusion': 'bg-slate-600'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getSensorIcon = (type) => {
    const icons = {
      'temperature': ThermometerSun,
      'humidity': Droplets,
      'noise_monitor': Volume2,
      'gas_detector': Wind,
      'air_quality': Wind,
      'co2': Wind,
      'co': AlertTriangle,
      'o2': Activity,
      'dust_particle': CircleDot
    };
    return icons[type] || Gauge;
  };

  // IoT Sensors Tab
  const renderSensorsTab = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      {sensorDashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4 text-center">
              <Wifi className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold text-blue-700">{sensorDashboard.total_sensors}</p>
              <p className="text-sm text-blue-600">Total Sensors</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-3xl font-bold text-green-700">{sensorDashboard.online_sensors}</p>
              <p className="text-sm text-green-600">Online</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
            <CardContent className="p-4 text-center">
              <XCircle className="w-8 h-8 mx-auto text-gray-600 mb-2" />
              <p className="text-3xl font-bold text-gray-700">{sensorDashboard.offline_sensors}</p>
              <p className="text-sm text-gray-600">Offline</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-8 h-8 mx-auto text-red-600 mb-2" />
              <p className="text-3xl font-bold text-red-700">{sensorDashboard.sensors_in_alert}</p>
              <p className="text-sm text-red-600">In Alert</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Alerts */}
      {sensorAlerts.length > 0 && (
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Active Sensor Alerts ({sensorAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sensorAlerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className={`p-4 rounded-lg border-l-4 ${
                alert.alert_level === 'critical' ? 'border-l-red-600 bg-red-50' :
                alert.alert_level === 'danger' ? 'border-l-orange-500 bg-orange-50' :
                'border-l-yellow-500 bg-yellow-50'
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold">{alert.title}</h4>
                    <p className="text-sm text-gray-600">{alert.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {alert.location} • {alert.value} {alert.unit}
                    </p>
                  </div>
                  <Badge className={getAlertColor(alert.alert_level)}>
                    {alert.alert_level}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Sensor Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Sensor Network</h3>
          <Button variant="outline" size="sm" onClick={() => setShowAddSensor(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Sensor
          </Button>
        </div>

        {sensorDashboard?.sensor_status?.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sensorDashboard.sensor_status.map((item) => {
              const SensorIcon = getSensorIcon(item.sensor?.sensor_type);
              return (
                <Card key={item.sensor?.id} className={`${
                  item.status === 'offline' ? 'opacity-50' :
                  item.status === 'critical' ? 'border-red-500 border-2' :
                  item.status === 'danger' ? 'border-orange-500 border-2' :
                  item.status === 'warning' ? 'border-yellow-500 border-2' : ''
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <SensorIcon className="w-5 h-5 text-blue-600" />
                        <span className="font-medium">{item.sensor?.name}</span>
                      </div>
                      <Badge className={getAlertColor(item.status)}>
                        {item.status}
                      </Badge>
                    </div>
                    <div className="text-center py-4">
                      <p className="text-4xl font-bold text-gray-800">
                        {item.latest_reading?.value?.toFixed(1) || '--'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {item.latest_reading?.unit || 'units'}
                      </p>
                    </div>
                    <div className="text-xs text-gray-500 flex justify-between">
                      <span>{item.sensor?.location}</span>
                      <span>{item.sensor?.sensor_type?.replace(/_/g, ' ')}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <Wifi className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No sensors configured yet</p>
            <Button className="mt-4" onClick={() => setShowAddSensor(true)}>
              <Plus className="w-4 h-4 mr-1" /> Add First Sensor
            </Button>
          </Card>
        )}
      </div>
    </div>
  );

  // Wearables Tab
  const renderWearablesTab = () => (
    <div className="space-y-6">
      {/* Fall Detection Alert */}
      {fallEvents.length > 0 && (
        <Card className="border-red-500 border-2 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2 animate-pulse">
              <AlertTriangle className="w-6 h-6" />
              FALL DETECTION ALERTS ({fallEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {fallEvents.map((event) => (
              <div key={event.id} className="p-4 bg-white rounded-lg border border-red-300">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-red-800">{event.worker_name}</h4>
                    <p className="text-sm text-red-700">
                      Location: {event.location} • Impact: {event.impact_severity}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(event.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Badge className="bg-red-600 text-white">Response Required</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Worker Status Grid */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Worker Health Monitoring</h3>
        {workerStatus.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workerStatus.map((worker) => (
              <Card key={worker.worker_id} className={`${
                worker.status === 'offline' ? 'opacity-50' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{worker.worker_name}</p>
                        <p className="text-xs text-gray-500">{worker.device?.device_type?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    <Badge className={worker.status === 'online' ? 'bg-green-500' : 'bg-gray-500'}>
                      {worker.status}
                    </Badge>
                  </div>
                  
                  {worker.latest_data && (
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="text-center p-2 bg-red-50 rounded-lg">
                        <Heart className="w-5 h-5 mx-auto text-red-500 mb-1" />
                        <p className="text-xl font-bold text-red-700">
                          {worker.latest_data.heart_rate || '--'}
                        </p>
                        <p className="text-xs text-gray-500">BPM</p>
                      </div>
                      <div className="text-center p-2 bg-blue-50 rounded-lg">
                        <Footprints className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                        <p className="text-xl font-bold text-blue-700">
                          {worker.latest_data.steps?.toLocaleString() || '--'}
                        </p>
                        <p className="text-xs text-gray-500">Steps</p>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded-lg">
                        <ThermometerSun className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                        <p className="text-xl font-bold text-orange-700">
                          {worker.latest_data.body_temperature || '--'}°
                        </p>
                        <p className="text-xs text-gray-500">Temp</p>
                      </div>
                      <div className="text-center p-2 bg-purple-50 rounded-lg">
                        <Activity className="w-5 h-5 mx-auto text-purple-500 mb-1" />
                        <p className="text-xl font-bold text-purple-700">
                          {worker.latest_data.fatigue_score || '--'}
                        </p>
                        <p className="text-xs text-gray-500">Fatigue</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <Watch className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No wearable devices assigned to workers</p>
          </Card>
        )}
      </div>

      {/* Device Inventory */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Watch className="w-5 h-5 text-purple-600" />
            Wearable Device Inventory ({wearables.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {wearables.length > 0 ? (
            <div className="space-y-2">
              {wearables.map((device) => (
                <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{device.name}</p>
                    <p className="text-sm text-gray-500">
                      {device.device_type?.replace(/_/g, ' ')} • SN: {device.serial_number}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      🔋 {device.battery_level}%
                    </Badge>
                    {device.assigned_worker_name ? (
                      <Badge className="bg-blue-500">{device.assigned_worker_name}</Badge>
                    ) : (
                      <Badge variant="outline">Unassigned</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No devices registered</p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Geofencing Tab
  const renderGeofencingTab = () => (
    <div className="space-y-6">
      {/* Geofence Alerts */}
      {geofenceAlerts.length > 0 && (
        <Card className="border-orange-300">
          <CardHeader>
            <CardTitle className="text-orange-700 flex items-center gap-2">
              <MapPinned className="w-5 h-5" />
              Zone Violation Alerts ({geofenceAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {geofenceAlerts.map((alert) => (
              <div key={alert.id} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-orange-800">{alert.worker_name}</p>
                    <p className="text-sm text-orange-700">{alert.description}</p>
                    <p className="text-xs text-gray-500">Zone: {alert.zone_name}</p>
                  </div>
                  <Badge className="bg-orange-500">{alert.alert_type}</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Zone Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Geofenced Zones</h3>
          <Button variant="outline" size="sm" onClick={() => setShowAddZone(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Zone
          </Button>
        </div>

        {zones.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {zones.map((zone) => (
              <Card key={zone.id} className="overflow-hidden">
                <div className={`h-2 ${getZoneTypeColor(zone.zone_type)}`} />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{zone.name}</h4>
                      <p className="text-sm text-gray-500">{zone.zone_type?.replace(/_/g, ' ')}</p>
                    </div>
                    <Badge variant="outline">
                      {zone.current_occupancy}/{zone.max_occupancy || '∞'}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{zone.description}</p>
                  
                  {zone.hazards?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-red-700 mb-1">⚠️ Hazards:</p>
                      <div className="flex flex-wrap gap-1">
                        {zone.hazards.map((hazard, i) => (
                          <Badge key={i} variant="outline" className="text-xs text-red-600">
                            {hazard}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {zone.required_ppe?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-blue-700 mb-1">Required PPE:</p>
                      <div className="flex flex-wrap gap-1">
                        {zone.required_ppe.map((ppe, i) => (
                          <Badge key={i} className="bg-blue-100 text-blue-800 text-xs">
                            {ppe}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <MapPin className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No geofenced zones configured</p>
            <Button className="mt-4" onClick={() => setShowAddZone(true)}>
              <Plus className="w-4 h-4 mr-1" /> Create First Zone
            </Button>
          </Card>
        )}
      </div>
    </div>
  );

  // Chemicals Tab
  const renderChemicalsTab = () => (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search chemicals by name, CAS number, or UN number..."
                value={chemicalSearch}
                onChange={(e) => setChemicalSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchChemicals()}
                className="pl-10"
              />
            </div>
            <Button onClick={searchChemicals}>
              <Search className="w-4 h-4 mr-1" /> Search SDS
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4 text-center">
            <FlaskConical className="w-8 h-8 mx-auto text-purple-600 mb-2" />
            <p className="text-3xl font-bold text-purple-700">{chemicals.length}</p>
            <p className="text-sm text-purple-600">Chemicals</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4 text-center">
            <Beaker className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <p className="text-3xl font-bold text-blue-700">{chemicalInventory.length}</p>
            <p className="text-sm text-blue-600">Inventory Items</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-orange-600 mb-2" />
            <p className="text-3xl font-bold text-orange-700">{exposureRecords.length}</p>
            <p className="text-sm text-orange-600">Exposures</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-4 text-center">
            <Skull className="w-8 h-8 mx-auto text-red-600 mb-2" />
            <p className="text-3xl font-bold text-red-700">
              {chemicals.filter(c => c.hazard_classes?.includes('toxic')).length}
            </p>
            <p className="text-sm text-red-600">Toxic</p>
          </CardContent>
        </Card>
      </div>

      {/* Chemical List */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Chemicals */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-purple-600" />
              Chemical Database
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {chemicals.length > 0 ? (
              <div className="space-y-2">
                {chemicals.map((chem) => (
                  <div
                    key={chem.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedChemical?.id === chem.id ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedChemical(chem)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{chem.name}</p>
                        <p className="text-xs text-gray-500">
                          CAS: {chem.cas_number || 'N/A'} • UN: {chem.un_number || 'N/A'}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {chem.hazard_classes?.includes('flammable') && (
                          <Flame className="w-4 h-4 text-red-500" />
                        )}
                        {chem.hazard_classes?.includes('toxic') && (
                          <Skull className="w-4 h-4 text-purple-500" />
                        )}
                        {chem.hazard_classes?.includes('corrosive') && (
                          <Droplets className="w-4 h-4 text-orange-500" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No chemicals in database</p>
            )}
          </CardContent>
        </Card>

        {/* SDS Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Safety Data Sheet
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {selectedChemical ? (
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-lg">{selectedChemical.name}</h4>
                  {selectedChemical.signal_word && (
                    <Badge className={selectedChemical.signal_word === 'Danger' ? 'bg-red-500' : 'bg-orange-500'}>
                      {selectedChemical.signal_word}
                    </Badge>
                  )}
                </div>

                {selectedChemical.hazard_statements?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700">Hazard Statements:</p>
                    <ul className="text-sm text-gray-600 list-disc list-inside">
                      {selectedChemical.hazard_statements.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedChemical.ppe_required?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-blue-700">Required PPE:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedChemical.ppe_required.map((ppe, i) => (
                        <Badge key={i} className="bg-blue-100 text-blue-800">{ppe}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedChemical.first_aid && Object.keys(selectedChemical.first_aid).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-green-700">First Aid:</p>
                    <div className="text-sm text-gray-600 space-y-1">
                      {Object.entries(selectedChemical.first_aid).map(([key, value]) => (
                        <p key={key}><strong>{key}:</strong> {value}</p>
                      ))}
                    </div>
                  </div>
                )}

                {selectedChemical.storage_requirements?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Storage:</p>
                    <ul className="text-sm text-gray-600 list-disc list-inside">
                      {selectedChemical.storage_requirements.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Select a chemical to view SDS details
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Exposures */}
      {exposureRecords.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertCircle className="w-5 h-5" />
              Recent Exposure Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {exposureRecords.slice(0, 5).map((record) => (
                <div key={record.id} className="p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{record.worker_name}</p>
                      <p className="text-sm text-gray-600">
                        {record.chemical_name} - {record.exposure_type}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(record.incident_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={
                        record.exposure_level === 'high' ? 'bg-red-500' :
                        record.exposure_level === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'
                      }>
                        {record.exposure_level}
                      </Badge>
                      {record.medical_attention && (
                        <Badge className="bg-red-600">Medical</Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Universal Page Header */}
      <PageHeader
        title="Safety Monitoring Hub"
        subtitle="IoT Sensors • Wearables • Geofencing • Chemical/SDS"
        icon={Radio}
        variant="blue"
        onRefresh={fetchAllData}
        loading={loading}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="sensors" className="flex items-center gap-2">
            <Wifi className="w-4 h-4" />
            <span className="hidden sm:inline">IoT Sensors</span>
          </TabsTrigger>
          <TabsTrigger value="wearables" className="flex items-center gap-2">
            <Watch className="w-4 h-4" />
            <span className="hidden sm:inline">Wearables</span>
          </TabsTrigger>
          <TabsTrigger value="geofencing" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">Geofencing</span>
          </TabsTrigger>
          <TabsTrigger value="chemicals" className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4" />
            <span className="hidden sm:inline">Chemicals</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sensors" className="mt-6">
          {renderSensorsTab()}
        </TabsContent>
        <TabsContent value="wearables" className="mt-6">
          {renderWearablesTab()}
        </TabsContent>
        <TabsContent value="geofencing" className="mt-6">
          {renderGeofencingTab()}
        </TabsContent>
        <TabsContent value="chemicals" className="mt-6">
          {renderChemicalsTab()}
        </TabsContent>
      </Tabs>

      {/* Refresh */}
      <div className="flex justify-center">
        <Button variant="outline" onClick={fetchAllData} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh All Data
        </Button>
      </div>
    </div>
  );
};

export default SafetyMonitoringHub;
